(() => {
  const ATTRIBUTION_STORAGE_KEY = "tragolvsbutiken-ads-attribution-v1";
  const LEAD_STORAGE_PREFIX = "tragolvsbutiken-lead-id:";
  const SUCCESS_REDIRECT_STORAGE_PREFIX = "tragolvsbutiken-lead-success-redirect:";
  const RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
  const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"];
  const CLICK_ID_PATTERN = /^[A-Za-z0-9._-]{1,256}$/;
  const PRIVACY_READY_MAX_ATTEMPTS = 40;
  const PRIVACY_READY_INTERVAL_MS = 50;

  function storageGet(storage, key) {
    try {
      return storage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function storageSet(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function storageRemove(storage, key) {
    try {
      storage.removeItem(key);
    } catch (error) {
      // Storage access must never interfere with the form.
    }
  }

  /**
   * @returns {"granted" | "denied" | "unknown"}
   */
  function marketingConsentState() {
    const privacy = window.Shopify?.customerPrivacy;
    if (typeof privacy?.marketingAllowed !== "function") {
      return "unknown";
    }
    return privacy.marketingAllowed() === true ? "granted" : "denied";
  }

  function validateClickId(value) {
    return typeof value === "string" && CLICK_ID_PATTERN.test(value) ? value : null;
  }

  function parseStoredAttribution(raw) {
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (
        !parsed ||
        typeof parsed !== "object" ||
        typeof parsed.expiresAt !== "number" ||
        !Number.isFinite(parsed.expiresAt) ||
        parsed.expiresAt <= Date.now()
      ) {
        return null;
      }

      const clickIds = {};
      for (const key of CLICK_ID_KEYS) {
        const value = validateClickId(parsed[key]);
        if (value) clickIds[key] = value;
      }
      return Object.keys(clickIds).length > 0
        ? {
            ...clickIds,
            expiresAt: parsed.expiresAt,
          }
        : null;
    } catch (error) {
      return null;
    }
  }

  function readAttribution() {
    const raw = storageGet(window.localStorage, ATTRIBUTION_STORAGE_KEY);
    const attribution = parseStoredAttribution(raw);
    if (!attribution && raw) {
      storageRemove(window.localStorage, ATTRIBUTION_STORAGE_KEY);
    }
    return attribution;
  }

  function clearAttribution() {
    storageRemove(window.localStorage, ATTRIBUTION_STORAGE_KEY);
  }

  function readClickIdsFromUrl() {
    const searchParams = new URL(window.location.href).searchParams;
    const clickIds = {};
    for (const key of CLICK_ID_KEYS) {
      const value = validateClickId(searchParams.get(key));
      if (value) clickIds[key] = value;
    }
    return clickIds;
  }

  function captureAttribution() {
    const clickIds = readClickIdsFromUrl();

    if (Object.keys(clickIds).length > 0) {
      const attribution = {
        ...clickIds,
        expiresAt: Date.now() + RETENTION_MS,
      };
      storageSet(window.localStorage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
      return attribution;
    }

    return readAttribution();
  }

  function createLeadId() {
    if (typeof window.crypto?.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
  }

  function getFormId(form, index) {
    return form.id || form.dataset.googleAdsFormId || `contact-form-${index}`;
  }

  function getLeadId(formId) {
    const key = `${LEAD_STORAGE_PREFIX}${formId}`;
    const existing = storageGet(window.sessionStorage, key);
    if (existing) return existing;

    const leadId = createLeadId();
    storageSet(window.sessionStorage, key, leadId);
    return leadId;
  }

  function updateFormFields() {
    const consent = marketingConsentState();
    let attribution = null;

    if (consent === "denied") {
      clearAttribution();
    } else if (consent === "granted") {
      attribution = captureAttribution();
    }

    const forms = document.querySelectorAll(
      "form:has([data-google-ads-lead-id]), [data-quote-studio-native-contact-form]",
    );

    forms.forEach((form, index) => {
      const formId = getFormId(form, index);
      const leadIdInput = form.querySelector("[data-google-ads-lead-id]");
      if (leadIdInput) leadIdInput.value = getLeadId(formId);

      const consentInput = form.querySelector("[data-google-ads-marketing-consent]");
      if (consentInput) {
        // Keep empty while the privacy API is unavailable so we never claim a false deny.
        consentInput.value = consent === "granted" ? "granted" : consent === "denied" ? "denied" : "";
      }

      for (const key of CLICK_ID_KEYS) {
        const input = form.querySelector(`[data-google-ads-click-id='${key}']`);
        if (input) input.value = attribution?.[key] || "";
      }
    });
  }

  function sendGenerateLead(leadId) {
    const eventParameters = {
      lead_id: leadId,
      lead_source: "shopify_contact_form",
    };

    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", eventParameters);
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(["event", "generate_lead", eventParameters]);
  }

  function redirectSuccessfulLeads() {
    document.querySelectorAll("[data-google-ads-lead-success]").forEach((marker, index) => {
      const formId = marker.dataset.googleAdsFormId || `contact-form-${index}`;
      const storageKey = `${SUCCESS_REDIRECT_STORAGE_PREFIX}${formId}`;
      if (storageGet(window.sessionStorage, storageKey)) return;

      const successUrl = marker.dataset.googleAdsLeadSuccessUrl;
      if (!successUrl) return;

      const resolvedUrl = new URL(successUrl, window.location.origin);
      if (resolvedUrl.origin !== window.location.origin) return;

      const leadId = getLeadId(formId);
      if (!storageSet(window.sessionStorage, storageKey, leadId)) return;
      sendGenerateLead(leadId);
      window.location.replace(resolvedUrl.href);
    });
  }

  function initialize() {
    updateFormFields();
    redirectSuccessfulLeads();
  }

  function whenPrivacyReady(callback) {
    if (marketingConsentState() !== "unknown") {
      callback();
      return;
    }

    let attempts = 0;
    let timer = 0;
    timer = setInterval(() => {
      attempts += 1;
      if (marketingConsentState() !== "unknown" || attempts >= PRIVACY_READY_MAX_ATTEMPTS) {
        clearInterval(timer);
        callback();
      }
    }, PRIVACY_READY_INTERVAL_MS);
  }

  document.addEventListener("visitorConsentCollected", updateFormFields);
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initialize();
        whenPrivacyReady(updateFormFields);
      },
      { once: true },
    );
  } else {
    initialize();
    whenPrivacyReady(updateFormFields);
  }
})();
